import { NavLink, useNavigate } from 'react-router';
import { useState } from 'react';
import {
  Bug,
  ChevronsUpDown,
  LogOut,
  Moon,
  Search,
  Sun,
  Laptop,
  Check,
  ListChecks,
  MoreHorizontal,
  UserRound
} from 'lucide-react';
import { BillableLogo } from '@/components/brand';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from '@/components/ui/menu';
import { navGroups, visibleNavGroups } from './nav';
import { useBillingNouns, useSession, useSignOut, useSwitchTeam } from '@/hooks/useSession';
import { DEMO_KIND_LABELS, useResetDemo } from '@/hooks/useDemo';
import { useGeneralSettings } from '@/hooks/useSettings';
import { useEnvironmentTag, useFeatureFlags } from '@/hooks/useConfig';
import { useTheme } from '@/components/theme';
import { initials } from '@/lib/format';
import { useBugReporter } from '@/features/bug-report/BugReporter';
import { setupInProgress, useSetup, useSetupAction } from '@/hooks/useSetup';
import { doneCount, useSetupSteps } from '@/features/setup';
import { cn } from '@/lib/utils';

export function Sidebar({ onOpenPalette }: { onOpenPalette: () => void }) {
  const session = useSession();
  const nouns = useBillingNouns();
  const settings = useGeneralSettings().data;
  const envTag = useEnvironmentTag();
  const flags = useFeatureFlags();
  const groups = visibleNavGroups(navGroups(nouns), {
    isAdmin: session.isAdmin,
    isPlatformAdmin: session.isPlatformAdmin,
    showOriginalTimesheets: Boolean(settings?.showOriginalTimesheetEntries),
    devOnly: flags.devOnly,
    features: session.flags
  });

  return (
    <nav
      aria-label="Main"
      className="flex h-full w-60 shrink-0 flex-col border-r border-[var(--line)] bg-[var(--surface)]"
    >
      <div className="flex h-14 items-center gap-2 px-4">
        <NavLink to="/timesheet" className="flex items-center gap-2 rounded-lg px-1 py-1">
          <BillableLogo className="size-7 text-[var(--text)]" />
          <span className="text-base font-semibold tracking-tight">
            Billable
            {envTag ? <span className="font-normal text-muted"> ({envTag})</span> : null}
          </span>
        </NavLink>
      </div>

      <div className="px-3 pb-2">
        <button
          type="button"
          onClick={onOpenPalette}
          className="flex w-full items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--surface-2)] px-2.5 py-1.5 text-sm text-[var(--text-subtle)] transition-colors hover:border-[var(--line-strong)] hover:text-[var(--text-muted)]"
        >
          <Search className="size-4" />
          <span className="flex-1 text-left">Search</span>
          <kbd className="rounded border border-[var(--line)] px-1.5 py-0.5 text-[11px] font-medium">
            ⌘K
          </kbd>
        </button>
      </div>

      <div className="scroll-thin flex-1 space-y-5 overflow-y-auto px-3 pb-4">
        <DemoPill />
        <SetupNavItem />
        {groups.map((group) => {
          const items = group.items;
          return (
            <div key={group.label}>
              <p className="px-2.5 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-subtle)]">
                {group.label}
              </p>
              <ul className="space-y-0.5">
                {items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm transition-colors duration-150',
                          isActive
                            ? 'bg-[var(--accent-soft)] font-medium text-[var(--accent)]'
                            : 'text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text)]'
                        )
                      }
                    >
                      <item.icon className="size-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <ReportBugButton />
      <AccountMenu />
    </nav>
  );
}

/**
 * The demo marker, above everything else in the nav. Only a demo team renders
 * it, so a real team's sidebar is untouched. Its menu goes back to the
 * starting data, or to /demo to pick another profile. The brand label stays
 * plain; this row is the whole signal.
 */
function DemoPill() {
  const session = useSession();
  const reset = useResetDemo();
  const navigate = useNavigate();
  const [confirmReset, setConfirmReset] = useState(false);
  const team = session.team;
  if (!team?.isDemo) return null;

  const label = team.demoKind ? DEMO_KIND_LABELS[team.demoKind] : 'Demo';

  return (
    <div>
      <div className="group/demo relative">
        <div className="flex items-center gap-2 rounded-xl bg-[#fffbeb] py-2 pl-2.5 pr-9 text-sm text-[#92400e] dark:bg-amber-950/40 dark:text-amber-300">
          <span className="font-medium">Demo</span>
          <span aria-hidden className="opacity-50">
            ·
          </span>
          <span className="truncate">{label}</span>
        </div>
        <Menu>
          <MenuTrigger asChild>
            <button
              type="button"
              aria-label="Demo options"
              className="absolute right-1.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-lg text-[#92400e]/70 transition-colors hover:bg-[#fef3c7] hover:text-[#92400e] dark:text-amber-300/70 dark:hover:bg-amber-900/40 dark:hover:text-amber-300"
            >
              <MoreHorizontal className="size-4" />
            </button>
          </MenuTrigger>
          <MenuContent align="start" side="right" className="w-44">
            <MenuItem onSelect={() => setConfirmReset(true)} disabled={reset.isPending}>
              Reset demo data
            </MenuItem>
            <MenuSeparator />
            <MenuItem onSelect={() => navigate('/demo')}>Start a different demo</MenuItem>
          </MenuContent>
        </Menu>
      </div>
      <ConfirmDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title="Reset the demo?"
        description="Everything goes back to the starting data."
        confirmLabel="Reset"
        destructive
        onConfirm={() => reset.mutate()}
      />
    </div>
  );
}

/**
 * "Setup", above the Work group, with the guide's progress as its badge
 * (`3 / 7`). Not a NavItem: it needs the setup state (absent for a team with
 * none yet) and carries its own Open / Hide setup / Start over menu.
 * Hiding setup takes this item away with the rail and chip; the way back is
 * "Show setup guide" in the account menu or on Settings. The command palette
 * does not list it.
 *
 * Gone for good once every step is done (onboarding is over), and never there
 * for someone who joined by invite (the query does not run for them).
 */
function SetupNavItem() {
  const setup = useSetup();
  const action = useSetupAction();
  const steps = useSetupSteps();
  const navigate = useNavigate();
  const state = setup.data;
  if (!setupInProgress(state) || state.hidden) return null;

  const done = doneCount(steps, state);
  const total = steps.length;

  return (
    <div>
      <ul className="space-y-0.5">
        <li className="group/setup relative">
          <NavLink
            to="/setup"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-xl py-2 pl-2.5 pr-9 text-sm transition-colors duration-150',
                isActive
                  ? 'bg-[var(--accent-soft)] font-medium text-[var(--accent)]'
                  : 'text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text)]'
              )
            }
          >
            <ListChecks className="size-4 shrink-0" />
            <span className="flex-1 truncate">Setup</span>
            <span className="tnum rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-muted)]">
              {done} / {total}
            </span>
          </NavLink>
          <Menu>
            <MenuTrigger asChild>
              <button
                type="button"
                aria-label="Setup options"
                className="absolute right-1.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-lg text-[var(--text-subtle)] opacity-0 transition-opacity hover:bg-[var(--surface-hover)] hover:text-[var(--text)] focus-visible:opacity-100 group-hover/setup:opacity-100 data-[state=open]:opacity-100"
              >
                <MoreHorizontal className="size-4" />
              </button>
            </MenuTrigger>
            <MenuContent align="start" side="right" className="w-44">
              <MenuItem onSelect={() => navigate('/setup')}>Open</MenuItem>
              <MenuItem onSelect={() => action.mutate('hide')}>Hide setup</MenuItem>
              <MenuSeparator />
              <MenuItem onSelect={() => action.mutate('startOver')}>Start over</MenuItem>
            </MenuContent>
          </Menu>
        </li>
      </ul>
    </div>
  );
}

/**
 * Pinned above the account menu rather than in a nav group: it is an action,
 * not a place, and it should be reachable from every screen without scrolling.
 */
function ReportBugButton() {
  const reporter = useBugReporter();
  return (
    <div className="border-t border-[var(--line)] px-3 pt-2">
      <button
        type="button"
        onClick={reporter.open}
        className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm text-[var(--text-muted)] transition-colors duration-150 hover:bg-[var(--surface-hover)] hover:text-[var(--text)]"
      >
        <Bug className="size-4 shrink-0" />
        <span className="truncate">Report bug</span>
      </button>
    </div>
  );
}

function AccountMenu() {
  const session = useSession();
  const signOut = useSignOut();
  const switchTeam = useSwitchTeam();
  const { theme, setTheme } = useTheme();
  const setup = useSetup();
  const setupAction = useSetupAction();
  // The quiet way back after "Hide setup": only while setup is hidden and not over.
  const canShowSetup = setupInProgress(setup.data) && setup.data.hidden;

  return (
    <div className="p-3 pt-1">
      <Menu>
        <MenuTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left transition-colors hover:bg-[var(--surface-hover)]"
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--surface-2)] text-sm font-medium text-[var(--text-muted)]">
              {session.user.name ? initials(session.user.name) : <UserRound className="size-4" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">
                {session.user.name || session.user.email || 'Account'}
              </span>
              <span className="block truncate text-sm text-muted">{session.team?.name}</span>
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-[var(--text-subtle)]" />
          </button>
        </MenuTrigger>
        <MenuContent align="start" side="top" className="w-[13.5rem]">
          {session.teams.length > 1 ? (
            <>
              <MenuLabel>Teams</MenuLabel>
              {session.teams.map((team) => (
                <MenuItem key={team.id} onSelect={() => switchTeam.mutate(team.id)}>
                  <span className="flex-1 truncate">{team.name}</span>
                  {team.id === session.team?.id ? <Check className="size-4" /> : null}
                </MenuItem>
              ))}
              <MenuSeparator />
            </>
          ) : null}

          <MenuLabel>Theme</MenuLabel>
          {(
            [
              ['light', 'Light', Sun],
              ['dark', 'Dark', Moon],
              ['system', 'System', Laptop]
            ] as const
          ).map(([value, label, Icon]) => (
            <MenuItem key={value} onSelect={() => setTheme(value)}>
              <Icon />
              <span className="flex-1">{label}</span>
              {theme === value ? <Check className="size-4" /> : null}
            </MenuItem>
          ))}

          <MenuSeparator />
          {canShowSetup ? (
            <>
              <MenuItem onSelect={() => setupAction.mutate('show')}>
                <ListChecks />
                Show setup guide
              </MenuItem>
              <MenuSeparator />
            </>
          ) : null}
          <MenuItem destructive onSelect={() => signOut.mutate()}>
            <LogOut />
            Sign out
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>
  );
}

export function SidebarSkeleton() {
  return (
    <div className="flex h-full w-60 shrink-0 flex-col gap-6 border-r border-[var(--line)] bg-[var(--surface)] p-4">
      <div className="skeleton h-8 w-32" />
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="skeleton h-8 w-full" />
        ))}
      </div>
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton h-8 w-full" />
        ))}
      </div>
      <div className="mt-auto skeleton h-10 w-full" />
    </div>
  );
}

export { Button };
